if (!customElements.get('product-info')) {
  // Cache section HTML per option_values URL so color/size toggles feel instant.
  const SECTION_CACHE = new Map();
  const SECTION_INFLIGHT = new Map();
  const SECTION_CACHE_MAX = 48;

  const cacheSet = (url, text) => {
    if (SECTION_CACHE.has(url)) SECTION_CACHE.delete(url);
    SECTION_CACHE.set(url, text);
    while (SECTION_CACHE.size > SECTION_CACHE_MAX) {
      SECTION_CACHE.delete(SECTION_CACHE.keys().next().value);
    }
  };

  const fetchSection = (url, signal) => {
    const cached = SECTION_CACHE.get(url);
    if (cached) return Promise.resolve(cached);

    if (SECTION_INFLIGHT.has(url)) return SECTION_INFLIGHT.get(url);

    const req = fetch(url, signal ? { signal } : undefined)
      .then((response) => {
        if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        cacheSet(url, text);
        return text;
      })
      .finally(() => {
        SECTION_INFLIGHT.delete(url);
      });

    SECTION_INFLIGHT.set(url, req);
    return req;
  };

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      prefetchScheduled = false;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this.schedulePrefetchRelatedSections();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        // Keep ATC enabled while fetching — disabling on every option click feels laggy.
        // Availability is applied again in handleUpdateProductInfo.
        this.productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        const fromCache = SECTION_CACHE.has(requestUrl);

        fetchSection(requestUrl, fromCache ? undefined : signal)
          .then((responseText) => {
            if (signal.aborted && !fromCache) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            this.schedulePrefetchRelatedSections();
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
          });
      }

      schedulePrefetchRelatedSections() {
        if (this.prefetchScheduled) return;
        this.prefetchScheduled = true;
        const run = () => {
          this.prefetchScheduled = false;
          this.prefetchRelatedSections();
        };
        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(run, { timeout: 1200 });
        } else {
          setTimeout(run, 200);
        }
      }

      prefetchRelatedSections() {
        const selects = this.variantSelectors;
        const productUrl = this.dataset.url;
        if (!selects || !productUrl || this.dataset.updateUrl === 'false') return;

        const current = selects.selectedOptionValues;
        if (!current.length) return;

        // Collect option-value IDs per option group (fieldset / select), DOM order.
        const groups = [];
        selects.querySelectorAll('fieldset').forEach((fieldset) => {
          const ids = Array.from(fieldset.querySelectorAll('input[data-option-value-id]'))
            .map((el) => el.dataset.optionValueId)
            .filter(Boolean);
          if (ids.length) groups.push(ids);
        });
        selects.querySelectorAll('select').forEach((select) => {
          const ids = Array.from(select.querySelectorAll('option[data-option-value-id]'))
            .map((el) => el.dataset.optionValueId)
            .filter(Boolean);
          if (ids.length) groups.push(ids);
        });
        if (!groups.length || groups.length !== current.length) return;

        const urls = [];
        groups.forEach((group, groupIndex) => {
          group.forEach((optionValueId) => {
            if (optionValueId === current[groupIndex]) return;
            const next = current.slice();
            next[groupIndex] = optionValueId;
            urls.push(this.buildRequestUrlWithParams(productUrl, next, false));
          });
        });

        // Warm nearby combinations (other colors / sizes) in the background.
        urls.slice(0, 16).forEach((url) => {
          if (SECTION_CACHE.has(url) || SECTION_INFLIGHT.has(url)) return;
          fetchSection(url).catch(() => {});
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const sourceGallery = this.querySelector('media-gallery');
        const destGallery = html.querySelector('media-gallery');

        // color-filter: Liquid returns a different media block per color.
        // Dawn only diffs the first viewer <ul>, leaving thumbnails/slider on the
        // previous color — replace the whole <media-gallery> instead.
        const useFullReplace =
          sourceGallery &&
          destGallery &&
          (sourceGallery.hasAttribute('data-color-filter') ||
            destGallery.hasAttribute('data-color-filter'));

        if (useFullReplace) {
          const sameColor =
            sourceGallery.getAttribute('data-color-filter') != null &&
            sourceGallery.getAttribute('data-color-filter') ===
              destGallery.getAttribute('data-color-filter');

          // Size-only change: keep gallery DOM, just ensure featured is active.
          if (sameColor) {
            if (variantFeaturedMediaId && typeof sourceGallery.setActiveMedia === 'function') {
              sourceGallery.setActiveMedia(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
            }
          } else {
            const imported = document.importNode(destGallery, true);
            sourceGallery.replaceWith(imported);

            const activate = () => {
              const gallery = this.querySelector('media-gallery');
              if (!gallery) return;
              if (variantFeaturedMediaId && typeof gallery.setActiveMedia === 'function') {
                gallery.setActiveMedia(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
              }
              const viewer = gallery.querySelector('[id^="GalleryViewer"]');
              if (viewer?.resetPages) viewer.resetPages();
              if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
            };

            if (customElements.get('media-gallery')) {
              requestAnimationFrame(activate);
            } else {
              customElements.whenDefined('media-gallery').then(() => requestAnimationFrame(activate));
            }
          }
        } else if (variantFeaturedMediaId) {
          const mediaGallerySource = this.querySelector('media-gallery ul');
          const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

          const refreshSourceData = () => {
            if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
            const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
            const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
            const sourceMap = new Map(
              mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
            );
            return [mediaGallerySourceItems, sourceSet, sourceMap];
          };

          if (mediaGallerySource && mediaGalleryDestination) {
            let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            const mediaGalleryDestinationItems = Array.from(
              mediaGalleryDestination.querySelectorAll('li[data-media-id]')
            );
            const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
            let shouldRefresh = false;

            // add items from new data not present in DOM
            for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
              if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
                mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
                shouldRefresh = true;
              }
            }

            // remove items from DOM not present in new data
            for (let i = 0; i < mediaGallerySourceItems.length; i++) {
              if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                mediaGallerySourceItems[i].remove();
                shouldRefresh = true;
              }
            }

            // refresh
            if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

            // if media galleries don't match, sort to match new data order
            mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
              }
            });
          }

          // Also sync thumbnail strip when present (Dawn only touched the viewer ul).
          const sourceThumbs = this.querySelector('media-gallery [id^="GalleryThumbnails"] ul');
          const destThumbs = html.querySelector('media-gallery [id^="GalleryThumbnails"] ul');
          if (sourceThumbs && destThumbs) {
            sourceThumbs.innerHTML = destThumbs.innerHTML;
            const gallery = this.querySelector('media-gallery');
            if (gallery) {
              gallery.elements = {
                liveRegion: gallery.querySelector('[id^="GalleryStatus"]'),
                viewer: gallery.querySelector('[id^="GalleryViewer"]'),
                thumbnails: gallery.querySelector('[id^="GalleryThumbnails"]'),
              };
              gallery.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                mediaToSwitch
                  .querySelector('button')
                  ?.addEventListener('click', gallery.setActiveMedia.bind(gallery, mediaToSwitch.dataset.target, false));
              });
            }
          }

          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
